import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Download, Users, Eye, MapPin, Image, Calendar, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VisitorStatistics = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [hourlyData, setHourlyData] = useState([]);
  const [itemStats, setItemStats] = useState([]);
  const [zones, setZones] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  
  // Helper to get local date string (YYYY-MM-DD)
  const getLocalDateString = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Date selections - use local date, not UTC
  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [startDate, setStartDate] = useState(getLocalDateString(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)));
  const [endDate, setEndDate] = useState(getLocalDateString());
  const [filterType, setFilterType] = useState('all');
  const [filterZone, setFilterZone] = useState('');

  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    fetchHourlyData();
  }, [selectedDate]);

  useEffect(() => {
    fetchItemStats();
  }, [startDate, endDate, filterType, filterZone]);

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([fetchSummary(), fetchZones(), fetchHourlyData(), fetchItemStats()]);
    setLoading(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchSummary(), fetchHourlyData(), fetchItemStats()]);
    setRefreshing(false);
    toast.success('Statistics refreshed');
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/staff/statistics/summary`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { _t: Date.now() }  // Cache buster
      });
      setSummary(response.data);
    } catch (error) {
      toast.error('Failed to load statistics summary');
    }
  };

  const fetchZones = async () => {
    try {
      const response = await axios.get(`${API}/zones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setZones(response.data);
    } catch (error) {
      console.error('Failed to load zones');
    }
  };

  const fetchHourlyData = async () => {
    try {
      setLoading(true);
      // Get timezone offset in minutes (e.g., -480 for UTC+8)
      const tzOffset = new Date().getTimezoneOffset();
      const response = await axios.get(`${API}/staff/statistics/hourly`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date: selectedDate, tz_offset: tzOffset, _t: Date.now() }
      });
      setHourlyData(response.data);
    } catch (error) {
      toast.error('Failed to load hourly data');
    } finally {
      setLoading(false);
    }
  };

  const fetchItemStats = async () => {
    try {
      const params = {
        start_date: startDate,
        end_date: endDate,
        _t: Date.now()  // Cache buster
      };
      if (filterType !== 'all') {
        params.item_type = filterType;
      }
      if (filterZone) {
        params.zone_id = filterZone;
      }
      
      const response = await axios.get(`${API}/staff/statistics/items`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setItemStats(response.data);
    } catch (error) {
      toast.error('Failed to load item statistics');
    }
  };

  const handleExport = async (exportType) => {
    try {
      const response = await axios.get(`${API}/staff/statistics/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: startDate,
          end_date: endDate,
          export_type: exportType
        },
        responseType: 'blob'
      });
      
      // Download file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `museum_stats_${exportType}_${startDate}_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const formatHour = (hour) => {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
  };

  const chartData = hourlyData.map(item => ({
    ...item,
    label: formatHour(item.hour)
  }));

  return (
    <div className="min-h-screen bg-background" data-testid="visitor-statistics-page">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/staff/dashboard')}
                variant="ghost"
                size="sm"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-heading font-bold">Visitor Statistics</h1>
                <p className="text-sm text-muted-foreground">Track visitor activity and engagement</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleRefresh}
                disabled={refreshing}
                variant="outline"
                size="sm"
                data-testid="refresh-button"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white border border-border rounded-lg p-6" data-testid="total-visitors-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Unique Visitors</p>
                <p className="text-3xl font-bold">{summary?.total_unique_visitors || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-border rounded-lg p-6" data-testid="total-views-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Eye className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Views</p>
                <p className="text-3xl font-bold">{summary?.total_views || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-border rounded-lg p-6" data-testid="exhibit-views-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Image className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Exhibit Views</p>
                <p className="text-3xl font-bold">{summary?.total_exhibit_views || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-border rounded-lg p-6" data-testid="zone-views-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <MapPin className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Zone Views</p>
                <p className="text-3xl font-bold">{summary?.total_zone_views || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Hourly Chart Section */}
        <div className="bg-white border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold">Hourly Visitors</h2>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="border border-border rounded-md px-3 py-1.5 text-sm"
                data-testid="hourly-date-picker"
              />
            </div>
          </div>
          
          <div className="h-80" data-testid="hourly-chart">
            {loading ? (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                Loading chart...
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="label" 
                    tick={{ fontSize: 12 }}
                    interval={2}
                  />
                  <YAxis 
                    allowDecimals={false}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                    name="Unique Visitors"
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Item Statistics Section */}
        <div className="bg-white border border-border rounded-lg p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <h2 className="text-lg font-semibold">Exhibit & Zone Statistics</h2>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground">From:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border border-border rounded-md px-3 py-1.5 text-sm"
                  data-testid="item-start-date"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground">To:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border border-border rounded-md px-3 py-1.5 text-sm"
                  data-testid="item-end-date"
                />
              </div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="border border-border rounded-md px-3 py-1.5 text-sm"
                data-testid="filter-type"
              >
                <option value="all">All Types</option>
                <option value="exhibit">Exhibits Only</option>
                <option value="zone">Zones Only</option>
              </select>
              <select
                value={filterZone}
                onChange={(e) => setFilterZone(e.target.value)}
                className="border border-border rounded-md px-3 py-1.5 text-sm"
                data-testid="filter-zone"
              >
                <option value="">All Zones</option>
                {zones.map(zone => (
                  <option key={zone.id} value={zone.id}>
                    {zone.title_tc || zone.title_en}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Items Table */}
          {itemStats.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground" data-testid="no-item-stats">
              No data available for the selected filters
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="item-stats-table">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium">Code</th>
                    <th className="text-left px-4 py-3 text-sm font-medium">Title</th>
                    <th className="text-left px-4 py-3 text-sm font-medium">Type</th>
                    <th className="text-right px-4 py-3 text-sm font-medium">Views</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {itemStats.map((item, index) => (
                    <tr key={index} data-testid={`item-row-${index}`}>
                      <td className="px-4 py-3">
                        <span className="font-mono text-lg font-bold text-primary">
                          {item.item_code}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium">{item.item_title}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.item_type === 'exhibit' 
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-orange-100 text-orange-700'
                        }`}>
                          {item.item_type === 'exhibit' ? 'Exhibit' : 'Zone'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-lg font-semibold">{item.view_count}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Export Section */}
        <div className="bg-white border border-border rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Export Data</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Download visitor statistics for the selected date range ({startDate} to {endDate})
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => handleExport('visitors')}
              variant="outline"
              data-testid="export-visitors-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Visitor Sessions (CSV)
            </Button>
            <Button
              onClick={() => handleExport('logs')}
              variant="outline"
              data-testid="export-logs-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Activity Logs (CSV)
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitorStatistics;
